// src/components/admin/JoinRequestsPanel.jsx
import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

// Pending join requests for one group. Approving or rejecting is always a manual click here.
export default function JoinRequestsPanel({ groupId, highlightRequestId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/api/groups/${groupId}/join-requests`)
      .then((res) => { if (!cancelled) setRequests(Array.isArray(res.data) ? res.data : []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'Could not load join requests.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [groupId]);

  const decide = async (requestId, action) => {
    setBusyId(requestId);
    setError('');
    setMessage('');
    try {
      const res = await api.patch(`/api/groups/${groupId}/join-requests/${requestId}`, { action });
      setRequests((list) => list.filter((r) => r._id !== requestId));
      setMessage(res.data?.message || 'Done.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update this request.');
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && !error && requests.length === 0 && !message) return null;

  return (
    <section className="border border-amber-300 bg-amber-50 rounded p-4 mb-6" aria-label="Pending join requests">
      <h2 className="font-semibold text-gray-800 mb-2">Pending join requests</h2>
      {loading && <p className="text-sm text-gray-600">Loading…</p>}
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {message && <p className="text-sm text-green-700 mb-2">{message}</p>}
      <ul className="space-y-2">
        {requests.map((r) => (
          <li
            key={r._id}
            className={`flex flex-wrap items-center justify-between gap-2 bg-white rounded p-2 ${r._id === highlightRequestId ? 'ring-2 ring-amber-500' : ''}`}
          >
            <span className="text-sm">
              <span className="block"><strong>Group:</strong> {r.groupName || 'Unknown group'}</span>
              <span className="block"><strong>Requester:</strong> {r.user?.username || 'Unknown user'}{r.user?.email && <span className="text-gray-600"> · {r.user.email}</span>}</span>
            </span>
            <span className="flex gap-2">
              <button
                type="button"
                onClick={() => decide(r._id, 'approve')}
                disabled={busyId === r._id}
                className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => decide(r._id, 'reject')}
                disabled={busyId === r._id}
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                Reject
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
